import { formatCurrency, formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { AgDefaultColDef, BaseComponent, ColumnTypesEnum } from '@r1-development/rps-shared-ui';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnsResetEvent,
  ColumnVisibleEvent,
  DragStoppedEvent,
  FilterChangedEvent,
  FilterModifiedEvent,
  GridReadyEvent,
  SelectionChangedEvent,
  SortChangedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { GridOptions, StatusPanelDef } from 'ag-grid-enterprise';
import { ColumnDefinitionProperties } from '../enums/column-definition-properties.enum';
import { DefaultColDef } from './default-configs';

// Supports default initialization values common to ag-grid components
@Injectable()
export abstract class AgGridBaseComponent<T> extends BaseComponent {
  abstract columnDefs: ColDef[];
  abstract localStoreKey: string;

  abstract grid: AgGridAngular;
  protected defaultColDef: ColDef = { ...DefaultColDef, ...AgDefaultColDef, enableRowGroup: true };
  protected gridMenu: { label: string; icon?: string; command: () => void }[] = [
    { label: 'Export All to Excel', command: () => this.exportFile('excel') },
  ];
  protected isFirstRender = false;

  private _gridOptions: GridOptions = {
    columnTypes: Object.fromEntries(Object.values(ColumnTypesEnum).map((ct) => [ct.toString(), {}])),
    tooltipShowDelay: 50,
    tooltipHideDelay: 50,
  };
  protected get gridOptions() {
    return this._gridOptions;
  }

  protected set gridOptions(gridOptions: GridOptions) {
    this._gridOptions = { ...gridOptions, ...this._gridOptions };
  }

  protected gridColumnApplied = false;
  protected filteredRecords = 0;
  protected gridFilterLength = 0;
  protected selectedRows: T[] = [];
  protected checkFilterTextLengthColumns: string[] = [];
  protected minimumFilterTextLength = 0;

  protected statusBar: {
    statusPanels: StatusPanelDef[];
  } = {
    statusPanels: [
      { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
      { statusPanel: 'agTotalRowCountComponent', align: 'left' },
      { statusPanel: 'agFilteredRowCountComponent' },
      { statusPanel: 'agSelectedRowCountComponent' },
      { statusPanel: 'agAggregationComponent' },
    ],
  };

  protected sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressPivotMode: true,
          suppressValues: true,
          suppressRowGroups: true,
        },
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
        toolPanelParams: {
          suppressExpandAll: true,
        },
      },
    ],
  };

  protected icons: { [key: string]: string } = {
    columns: `<span class="cm-icon sidebar-icon"><svg viewBox="10 10 12 12" xmlns="http://www.w3.org/2000/svg"><path d="M9 12.5C9 11.7267 9.62672 11.1 10.4 11.1H21.6C22.3722 11.1 23 11.7267 23 12.5V19.5C23 20.2722 22.3722 20.9 21.6 20.9H10.4C9.62672 20.9 9 20.2722 9 19.5V12.5ZM18.8 19.85H21.6C21.7925 19.85 21.95 19.6925 21.95 19.5V12.5C21.95 12.3066 21.7925 12.15 21.6 12.15H18.8V19.85ZM17.75 12.15H14.25V19.85H17.75V12.15ZM13.2 19.85V12.15H10.4C10.2066 12.15 10.05 12.3066 10.05 12.5V19.5C10.05 19.6925 10.2066 19.85 10.4 19.85H13.2Z"/></svg></span>`,
    filter: `<span class="cm-icon sidebar-icon"><svg viewBox="10 10 12 12" xmlns="http://www.w3.org/2000/svg"><path d="M22.882 10.6055C22.6714 10.1549 22.2285 9.87518 21.7226 9.87518H10.2755C9.77133 9.87518 9.32728 10.1549 9.1171 10.6055C8.91263 11.0435 8.97927 11.5475 9.29092 11.9204L13.8134 17.4738V19.3252C13.8134 19.6683 13.9824 19.9896 14.2651 20.1851L16.516 21.9227C16.6861 22.0592 16.9185 22.1248 17.1318 22.1248C17.7136 22.1248 18.1869 21.6548 18.1869 21.0767V17.4738L22.7122 11.915C23.0214 11.5475 23.087 11.0435 22.882 10.6055ZM17.0716 16.741C16.9444 16.8972 16.875 17.0926 16.875 17.2941V20.5416L15.125 19.1912V17.3179C15.125 17.1164 15.0556 16.9209 14.9284 16.7648L10.3882 11.1887H21.6097L17.0716 16.741Z"/></svg></span>`,
  };

  protected readonly defaultNumberColumnParams: ColDef = {
    cellClass: 'ag-right-aligned-cell',
    filter: 'agNumberColumnFilter',
  };

  protected readonly defaultCurrencyColumnParams: ColDef = {
    ...this.defaultNumberColumnParams,
    valueFormatter: (param: ValueFormatterParams<unknown>) =>
      param.data[param.colDef.field] != null ? formatCurrency(param.data[param.colDef.field], 'en-US', '$') : null,
  };

  protected readonly defaultDateColumnParams: ColDef = {
    cellClass: 'text-center',
    filter: 'agDateColumnFilter',
    valueGetter: (param: ValueGetterParams<unknown>) =>
      param.data[param.colDef.field] ? formatDate(param.data[param.colDef.field], 'MM/dd/yyyy', 'en-US') : null,
  };

  protected readonly defaultRowNumberColumnParams: ColDef = {
    valueGetter: 'node.rowIndex + 1',
    headerName: 'Row',
    colId: 'Row',
    filter: false,
    sortable: false,
    pinned: 'left',
    lockPinned: true,
    cellClass: 'ag-right-aligned-cell',
    suppressHeaderMenuButton: true,
    headerTooltip: '',
    hide: false,
    lockVisible: true,
    resizable: false,
    width: 100,
    maxWidth: 100,
    lockPosition: true,
  };

  protected readonly defaultTextColumnParams: ColDef = {
    filter: 'agTextColumnFilter',
  };

  protected getRowStyle = (params) => {
    if (params.node.rowIndex % 2 !== 0) {
      return { background: '#faf9fb' };
    }
    return null;
  };

  /** @deprecated Use initGridMenu() instead. mat-menu opens via [matMenuTriggerFor]; no manual toggle needed. */
  protected openGridControl(event: MouseEvent, menu: unknown, isOnlyExportExcel = false): void {
    this.initGridMenu(isOnlyExportExcel);
  }

  protected onColumnChanged(
    params:
      | ColumnVisibleEvent<any, any>
      | ColumnMovedEvent<any, any>
      | ColumnPinnedEvent<any, any>
      | ColumnResizedEvent<any, any>
      | SortChangedEvent<any, any>
      | DragStoppedEvent<any, any>
  ): void {
    if (!this.isFirstRender) {
      return;
    }
    const columnDefs: ColDef[] = params.api.getColumnDefs();
    const store: ColDef[] = [];
    columnDefs.forEach((col) => {
      store.push({
        field: col[ColumnDefinitionProperties.Field],
        sort: col[ColumnDefinitionProperties.Sort],
        width: col[ColumnDefinitionProperties.Width],
        pinned: col[ColumnDefinitionProperties.Pinned] || false,
        hide: col[ColumnDefinitionProperties.Hide] || false,
      });
    });
    this.gridColumnApplied = true;
    const storeKey = this.localStoreKey ? this.localStoreKey : params.context;
    localStorage.setItem(`${storeKey}-column`, JSON.stringify(store));
  }

  protected onGridReady(params: GridReadyEvent) {
    const storeKey = this.localStoreKey || params.context;
    const columnState = JSON.parse(localStorage.getItem(`${storeKey}-column`));
    if (columnState?.length && columnState.every((col: any) => col?.colId != null)) {
      params.api.applyColumnState({
        state: columnState,
        applyOrder: true,
      });
    }
    // Populate gridMenu eagerly so mat-menu has items on first open.
    // (Previously populated lazily in openGridControl via PrimeNG menu.toggle).
    // this.initGridMenu();
  }

  protected initGridMenu(isOnlyExportExcel = false): void {
    this.gridMenu = [
      {
        label: 'Export All to Excel',
        command: () => this.exportFile('excel'),
      },
    ];
    if (!isOnlyExportExcel) {
      this.gridMenu = [...this.gridMenu];
    }
  }

  protected resetColumns(): void {
    this.grid.api.resetColumnState();
    this.gridColumnApplied = false;
    localStorage.removeItem(`${this.localStoreKey}-column`);
  }

  protected onFirstDataRendered(): void {
    const filterModel = JSON.parse(localStorage.getItem(`${this.localStoreKey}-filter`));
    const columnModel = JSON.parse(localStorage.getItem(`${this.localStoreKey}-column`));
    this.gridFilterLength = filterModel ? Object.keys(filterModel).length || 0 : 0;
    this.gridColumnApplied = !!columnModel;
    if (filterModel) {
      this.grid.api.setFilterModel(filterModel);
      this.filteredRecords = this.grid.api.getDisplayedRowCount() || 0;
    }
    if (columnModel) {
      const columnDefs = this.grid.api.getColumnDefs();
      const retainColumns = columnModel.reduce((cols, col) => {
        const selectedCol = columnDefs.find((def) => def[ColumnDefinitionProperties.Field] === col.field);
        cols.push({ ...selectedCol, sort: col.sort, width: col.width, pinned: col.pinned, hide: col.hide });
        return cols;
      }, []);
      this.grid.api.setGridOption('columnDefs', retainColumns);
    }
    this.isFirstRender = true;
  }

  protected rowDataUpdated(params): void {
    if (this.filteredRecords > 0) {
      this.onFilterChanged(params);
    }
  }

  protected exportFile(type: string): void {
    if (type === 'excel') {
      this.grid.api.exportDataAsExcel();
    }
  }

  protected resetFilter(): void {
    this.grid.api.setFilterModel(null);
    this.grid.api.onFilterChanged();
    localStorage.removeItem('filterModel');
  }

  protected onFilterChanged(event: FilterChangedEvent<T>): void {
    this.filteredRecords = event.api.getDisplayedRowCount() || 0;
    const filterModel = event.api?.getFilterModel() || {};
    const filterModelStorage = JSON.parse(localStorage.getItem(`${this.localStoreKey}-filter`));
    if (filterModelStorage) {
      Object.keys(filterModelStorage).forEach((key) => {
        if (filterModel[key]?.values?.length === 0) {
          filterModel[key].values = filterModelStorage[key].values;
        }
      });
    }
    this.gridFilterLength = filterModel ? Object.keys(filterModel).length || 0 : 0;
    localStorage.setItem(`${this.localStoreKey}-filter`, JSON.stringify(filterModel));
  }

  protected onQuickFilterChanged(text: string): void {
    this.grid.api.setGridOption('quickFilterText', text);
  }

  protected onSelectionChanged(event: SelectionChangedEvent<T>): void {
    this.selectedRows = [...event.api.getSelectedRows()];
  }

  protected onColumnsReset = (_event: ColumnsResetEvent) => {
    this.resetColumns();
  };

  protected dateTransform(
    value: string,
    includeTime?: boolean,
    setDefaultValue?: string,
    includeTimeWithSeconds?: boolean
  ) {
    let dt = '';
    if (includeTimeWithSeconds) {
      dt = formatDate(value, 'MM/dd/yyyy hh:mm:ss a', 'en-US');
    } else {
      dt = formatDate(value, 'MM/dd/yyyy' + (includeTime ? ' hh:mm:ss a' : ''), 'en-US');
    }

    if (setDefaultValue && !dt) {
      dt = setDefaultValue;
    }
    return dt;
  }

  protected toggleGridLoadingOverlay(flag: boolean): void {
    if (flag) {
      this.grid?.api?.showLoadingOverlay();
    } else {
      this.grid?.api?.hideOverlay();
    }
  }

  /** For server side text filters, you might not want to let the user hit the server until a certain number of characters are entered. */
  protected toggleFilterApplyButton = (event: FilterModifiedEvent<T>): void => {
    const selectedColumn = (event.column as any).colId;
    const filterModel = (event.filterInstance as any).getModelFromUi();
    let applyDisabled = false;
    if (
      filterModel?.filterType === 'text' &&
      ['contains', 'notContains'].includes(filterModel.type) &&
      this.checkFilterTextLengthColumns.includes(selectedColumn)
    ) {
      applyDisabled = filterModel.filter.length < this.minimumFilterTextLength;
    }

    const el = document.querySelector('.ag-filter-apply-panel [ref=applyFilterButton]');
    if (el) {
      if (applyDisabled) {
        el['disabled'] = 'disabled';
        el['title'] = `Minimum of ${this.minimumFilterTextLength} characters required`;
      } else {
        el.removeAttribute('disabled');
        el.removeAttribute('title');
      }
    }
  };
}
